import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useCookieConsent } from "@/components/CookieConsent";
declare global {
  interface Window {
    gtag: (...args: unknown[]) => void;
    dataLayer: unknown[];
    __gaLoaded?: boolean;
    __gaLoading?: boolean;
  }
}

const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID || "";
const GOOGLE_ADS_ID = "AW-701305785";

const loadGoogleAnalytics = (): Promise<boolean> => {
  return new Promise((resolve) => {
    if (!GA_MEASUREMENT_ID) {
      console.log("Google Analytics: No measurement ID configured");
      resolve(false);
      return;
    }

    if (window.__gaLoaded) {
      resolve(true);
      return;
    }

    if (window.__gaLoading) {
      const checkInterval = setInterval(() => {
        if (window.__gaLoaded) {
          clearInterval(checkInterval);
          resolve(true);
        }
      }, 100);
      setTimeout(() => {
        clearInterval(checkInterval);
        resolve(false);
      }, 5000);
      return;
    }

    window.__gaLoading = true;

    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag(...args: unknown[]) {
      window.dataLayer.push(args);
    };
    window.gtag("js", new Date());

    // Do NOT call updateConsentMode here — CookieConsent already set the correct
    // analytics + advertising flags. Forcing advertising=false here was overwriting
    // AdSense consent and killing personalized/NPA fill for free users.

    window.gtag("config", GA_MEASUREMENT_ID, {
      anonymize_ip: true,
      cookie_flags: "SameSite=None;Secure",
    });

    // NOTE: Google Ads tag is NOT configured here — it requires separate advertising consent

    const script = document.createElement("script");
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    script.async = true;

    script.onload = () => {
      window.__gaLoaded = true;
      window.__gaLoading = false;
      console.log("Google Analytics: Loaded successfully");
      resolve(true);
    };

    script.onerror = () => {
      window.__gaLoading = false;
      console.log("Google Analytics: Failed to load (possibly blocked)");
      resolve(false);
    };

    document.head.appendChild(script);
  });
};

/**
 * Configure Google Ads conversion tag — only when advertising consent is granted.
 * Separated from GA to prevent loading ads tracking with analytics-only consent.
 */
const configureGoogleAdsTag = () => {
  if (window.gtag && GOOGLE_ADS_ID) {
    window.gtag("config", GOOGLE_ADS_ID);
    console.log("Google Ads tag: Configured with advertising consent");
  }
};

export const trackPageView = (path: string) => {
  if (window.gtag && GA_MEASUREMENT_ID) {
    window.gtag("event", "page_view", {
      page_path: path,
      page_location: window.location.href,
    });
  }
};

export const trackEvent = (
  eventName: string,
  eventParams?: Record<string, unknown>
) => {
  if (window.gtag && GA_MEASUREMENT_ID) {
    window.gtag("event", eventName, eventParams);
  }
};

export const trackConversion = (conversionId: string) => {
  if (window.gtag) {
    window.gtag("event", "conversion", { send_to: conversionId });
  }
};

export const AnalyticsProvider = ({ children }: { children: React.ReactNode }) => {
  const { preferences, hasConsented } = useCookieConsent();
  const location = useLocation();
  const analyticsInitialized = useRef(false);
  const adsTagConfigured = useRef(false);
  const hasAnalyticsConsent = hasConsented && preferences.analytics;
  const hasAdvertisingConsent = hasConsented && preferences.advertising;

  useEffect(() => {
    if (!hasAnalyticsConsent) {
      return;
    }

    if (analyticsInitialized.current) {
      return;
    }

    const initAnalytics = async () => {
      const loaded = await loadGoogleAnalytics();
      if (loaded) {
        analyticsInitialized.current = true;
        trackPageView(location.pathname);
      }
    };

    initAnalytics();
  }, [hasAnalyticsConsent, location.pathname]);

  // Separate effect: only configure Google Ads tag when advertising consent is granted
  useEffect(() => {
    if (!hasAdvertisingConsent || adsTagConfigured.current) {
      return;
    }

    if (analyticsInitialized.current) {
      configureGoogleAdsTag();
      adsTagConfigured.current = true;
    }
  }, [hasAdvertisingConsent]);

  useEffect(() => {
    if (analyticsInitialized.current && hasAnalyticsConsent) {
      trackPageView(location.pathname);
    }
  }, [location.pathname, hasAnalyticsConsent]);

  return <>{children}</>;
};

export default AnalyticsProvider;
